// Photos taken with a phone, shrunk in the browser before they are sent: the
// API refuses requests over 1 MB (its proxy), and a smaller photo uploads
// faster on mobile. 2000 px on the longest side keeps labels and serial
// numbers readable. One photo per request (see uploadPhotos callers).
export const MAX_PHOTO_SIDE = 2000;
// Each request stays under the 1 MB of the API's proxy
export const MAX_PHOTO_BYTES = 900 * 1024;
const QUALITIES = [0.85, 0.75, 0.65, 0.55];

// The picture, upright (orientation of the camera applied)
const decode = async (file) => {
  if (typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(file, { imageOrientation: "from-image" });
    } catch {
      /* format not decoded this way: the <img> route below */
    }
  }
  const url = URL.createObjectURL(file);
  try {
    const image = new Image();
    image.src = url;
    await image.decode();
    return image;
  } finally {
    URL.revokeObjectURL(url);
  }
};

const toJpeg = (canvas, quality) =>
  new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));

// A smaller JPEG of the photo; the file itself when it is already small, not
// a picture the browser can read, or an animation (GIF)
export async function shrinkPhoto(file) {
  if (!file?.type?.startsWith("image/") || file.type === "image/gif")
    return file;
  let picture;
  try {
    picture = await decode(file);
  } catch {
    return file;
  }
  const width = picture.width || picture.naturalWidth;
  const height = picture.height || picture.naturalHeight;
  const scale = Math.min(1, MAX_PHOTO_SIDE / Math.max(width, height));
  if (scale === 1 && file.size <= MAX_PHOTO_BYTES) {
    picture.close?.();
    return file;
  }
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(width * scale);
  canvas.height = Math.round(height * scale);
  canvas.getContext("2d").drawImage(picture, 0, 0, canvas.width, canvas.height);
  picture.close?.();
  let blob = null;
  for (const quality of QUALITIES) {
    blob = await toJpeg(canvas, quality);
    if (blob && blob.size <= MAX_PHOTO_BYTES) break;
  }
  if (!blob || blob.size >= file.size) return file;
  const name = (file.name || "photo").replace(/\.[^.]*$/, "") + ".jpg";
  return new File([blob], name, {
    type: "image/jpeg",
    lastModified: file.lastModified,
  });
}

export const shrinkPhotos = (files) =>
  Promise.all(Array.from(files || []).map(shrinkPhoto));

// Message for a photo refused by the server (too big for its proxy)
export const photoErrorMessage = (error) =>
  error?.response?.status === 413
    ? "Une photo est trop lourde pour le serveur, même réduite."
    : "Les photos n’ont pas pu être envoyées.";
